import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import BaseUrl from '../../Url/BaseUrl';

const STATIC_STATES = [
  {
    stateCode: "MP",
    stateName: "Madhya Pradesh",
    hindi: "मध्य प्रदेश",
    displayName: "Madhya Pradesh"
  }
];

const getWithLanguageSupport = (item, language = 'en') => {
  if (!item) return null;
  if (language === 'hi' && item.hindi) {
    return { ...item, displayName: item.hindi };
  }
  return {
    ...item,
    displayName: item.stateName || item.districtName || item.operatorName ||
                item.busStandName || item.zoneName || item.busName ||
                item.name || item.cityVillageName || item.cityVillageCode || ''
  };
};

export const fetchOperators = createAsyncThunk(
  'bus/fetchOperators',
  async (_, { getState }) => {
    const state = getState();
    const language = state.bus.language || 'en';
    const response = await axios.get(`${BaseUrl.baseurl}BusOperators`);
    const operators = response.data.map(operator => getWithLanguageSupport(operator, language));
    return operators;
  }
);

export const fetchRoutePaths = createAsyncThunk(
  'bus/fetchRoutePaths',
  async (_, { getState }) => {
    const state = getState();
    const language = state.bus.language || 'en';
    const response = await axios.get(`${BaseUrl.baseurl}RoutePaths`);
    const routePaths = response.data.map(routePath => getWithLanguageSupport(routePath, language));
    return routePaths;
  }
);

export const fetchRoutePathVillages = createAsyncThunk(
  'bus/fetchRoutePathVillages',
  async (_, { getState }) => {
    const state = getState();
    const language = state.bus.language || 'en';
    const response = await axios.get(`${BaseUrl.baseurl}RoutePathVillages`);
    const villages = response.data.map(village => getWithLanguageSupport(village, language));
    return villages;
  }
);

export const checkBusNumberAvailability = createAsyncThunk(
  'bus/checkBusNumberAvailability',
  async (busNo, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BaseUrl.baseurl}Buses/check-availability/${busNo}`);
      return response.data;
    } catch (error) {
      try {
        const response = await axios.get(`${BaseUrl.baseurl}Buses`);
        const buses = response.data;
        const isTaken = buses.some(bus => bus.busNo === parseInt(busNo));
        return {
          busNo,
          available: !isTaken,
          message: isTaken ? 'Bus number already exists' : 'Bus number is available'
        };
      } catch (fetchError) {
        return rejectWithValue(fetchError.response?.data?.message || fetchError.message || 'Failed to check availability');
      }
    }
  }
);

export const registerNewBus = createAsyncThunk(
  'bus/registerNewBus',
  async (busData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BaseUrl.baseurl}Buses`, busData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to register bus');
    }
  }
);

export const fetchStates = createAsyncThunk(
  'bus/fetchStates',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const language = state.bus.language || 'en';
      const response = await axios.get(`${BaseUrl.baseurl}States`);
      const states = response.data.map(stateItem => getWithLanguageSupport(stateItem, language));
      return states;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch states');
    }
  }
);

export const fetchDistrictsByState = createAsyncThunk(
  'bus/fetchDistrictsByState',
  async ({ stateCode, type }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const language = state.bus.language || 'en';
      const response = await axios.get(`${BaseUrl.baseurl}Districts/GetDistrictsByStateId?StateCode=${stateCode}`);
      const districts = response.data.map(district => getWithLanguageSupport(district, language));
      return { stateCode, type, districts };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch districts');
    }
  }
);

export const fetchCitiesByDistrict = createAsyncThunk(
  'bus/fetchCitiesByDistrict',
  async ({ districtCode, type }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const language = state.bus.language || 'en';
      const response = await axios.get(`${BaseUrl.baseurl}CityVillages/GetCityVillagesByDistrictCode?DistrictCode=${districtCode}`);
      const cities = response.data.map(city => getWithLanguageSupport(city, language));
      return { districtCode, type, cities };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch cities');
    }
  }
);

// ============================================================
// SEARCH BUSES - UPDATED for new API structure
// API Response has: cityVillages, routePaths, arrival[], return[], busStands[], buses[]
//
// arrival[]  = FORWARD ROUTE, filtered per bus by busNo
//              FIRST entry (ascending by time) = departure from source
//              LAST  entry (ascending by time) = arrival at destination
//
// return[]   = RETURN ROUTE, filtered per bus by busNo
//              LAST  entry's departureTime = final return departure time
// ============================================================
export const searchBuses = createAsyncThunk(
  'bus/searchBuses',
  async ({ fromCity, toCity }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const language = state.bus.language || 'en';
      const response = await axios.post(
        `${BaseUrl.baseurl}RoutePathVillages/RoutePathVillageSearch`,
        { fromCityVillageCode: fromCity, toCityVillageCode: toCity },
        { headers: { 'accept': 'text/plain', 'Content-Type': 'application/json' } }
      );
      const fullResponse = response.data;
      console.log(fullResponse)
      // New API structure
      const buses = fullResponse.buses || [];
      const arrivalData = fullResponse.arrival || [];
      const returnData = fullResponse.return || [];

      const processedBuses = buses.map(bus => {
        const processedBus = { ...bus };
        const processObject = (obj) => {
          if (!obj) return obj;
          return getWithLanguageSupport(obj, language);
        };
        processedBus.operator = processObject(bus.operator);
        processedBus.state = processObject(bus.state);
        processedBus.zone = processObject(bus.zone);
        processedBus.route = processObject(bus.route);
        processedBus.busStand = processObject(bus.busStand);
        processedBus.toBusStand = processObject(bus.toBusStand);
        const processedBusWithLanguage = getWithLanguageSupport(bus, language);
        return { ...processedBus, ...processedBusWithLanguage };
      });

      return {
        ...fullResponse,
        buses: processedBuses,
        arrival: arrivalData,
        return: returnData,
        fullApiResponse: fullResponse
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to search buses');
    }
  }
);

export const fetchBusDetails = createAsyncThunk(
  'bus/fetchBusDetails',
  async (busNo, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const language = state.bus.language || 'en';
      const response = await axios.get(`${BaseUrl.baseurl}Buses/${busNo}`);
      const bus = getWithLanguageSupport(response.data, language);
      return bus;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch bus details');
    }
  }
);

export const fetchZones = createAsyncThunk(
  'bus/fetchZones',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const language = state.bus.language || 'en';
      const response = await axios.get(`${BaseUrl.baseurl}Zones`);
      const zones = response.data.map(zone => getWithLanguageSupport(zone, language));
      return zones;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch zones');
    }
  }
);

const initialState = {
  language: localStorage.getItem('preferred-language') || 'hi', // ✅ CHANGED: Default to Hindi instead of English
  operators: [],
  routePaths: [],
  routePathVillages: [],
  zones: [],
  filteredZones: [],
  currentBus: null,
  busNumberAvailability: null,

  search: {
    formData: {
      fromState: '',
      fromDistrict: '',
      fromCity: '',
      toState: '',
      toDistrict: '',
      toCity: '',
      passengers: 1
    },
    states: STATIC_STATES,
    districts: { fromDistricts: [], toDistricts: [] },
    cities: { fromCities: [], toCities: [] },
    searchResults: [],
    expandedBus: null,
    selectedBus: null,
    currentBooking: null,
    bookingHistory: [],
    fullApiResponse: null,
    searchAttempted: false,
    filters: {
      minPrice: 0,
      maxPrice: 5000,
      busTypes: [],
      departureTime: null,
      arrivalTime: null,
      sortBy: 'departure',
      sortOrder: 'asc'
    }
  },

  loading: {
    states: false,
    districts: false,
    cities: false,
    buses: false,
    operators: false,
    routePaths: false,
    routePathVillages: false,
    zones: false,
    booking: false,
    checkingAvailability: false,
    registration: false
  },

  errors: {
    states: null,
    districts: null,
    cities: null,
    buses: null,
    operators: null,
    routePaths: null,
    routePathVillages: null,
    zones: null,
    booking: null,
    registration: null
  },

  successMessage: null
};

const busSlice = createSlice({
  name: 'bus',
  initialState,
  reducers: {
    setLanguage: (state, action) => {
      state.language = action.payload;
      localStorage.setItem('preferred-language', action.payload);
    },
    clearMessages: (state) => {
      state.errors = initialState.errors;
      state.successMessage = null;
    },
    setCurrentBus: (state, action) => {
      state.currentBus = action.payload;
    },
    filterZonesByStateCode: (state, action) => {
      const stateCode = action.payload;
      if (stateCode) {
        state.filteredZones = state.zones.filter(zone => zone.stateCode === stateCode);
      } else {
        state.filteredZones = [];
      }
    },
    clearBusNumberAvailability: (state) => {
      state.busNumberAvailability = null;
    },
    setSearchFormData: (state, action) => {
      state.search.formData = { ...state.search.formData, ...action.payload };
    },
    resetSearchFormData: (state) => {
      state.search.formData = {
        fromState: '',
        fromDistrict: '',
        fromCity: '',
        toState: '',
        toDistrict: '',
        toCity: '',
        passengers: 1
      };
      state.search.districts = { fromDistricts: [], toDistricts: [] };
      state.search.cities = { fromCities: [], toCities: [] };
    },
    resetSearchPage: (state) => {
      state.search.formData = initialState.search.formData;
      state.search.states = initialState.search.states;
      state.search.districts = initialState.search.districts;
      state.search.cities = initialState.search.cities;
      state.search.searchResults = initialState.search.searchResults;
      state.search.fullApiResponse = initialState.search.fullApiResponse;
      state.search.searchAttempted = false;
      state.search.expandedBus = initialState.search.expandedBus;
      state.search.selectedBus = initialState.search.selectedBus;
      state.loading.states = false;
      state.loading.districts = false;
      state.loading.cities = false;
      state.loading.buses = false;
      state.errors.states = null;
      state.errors.districts = null;
      state.errors.cities = null;
      state.errors.buses = null;
    },
    setSearchStates: (state, action) => {
      state.search.states = action.payload;
    },
    setSearchDistricts: (state, action) => {
      const { type, districts } = action.payload;
      if (type === 'from') {
        state.search.districts.fromDistricts = districts;
      } else {
        state.search.districts.toDistricts = districts;
      }
    },
    setSearchCities: (state, action) => {
      const { type, cities } = action.payload;
      if (type === 'from') {
        state.search.cities.fromCities = cities;
      } else {
        state.search.cities.toCities = cities;
      }
    },
    setSearchResults: (state, action) => {
      state.search.searchResults = action.payload;
    },
    setExpandedBus: (state, action) => {
      state.search.expandedBus = action.payload;
    },
    selectSearchBus: (state, action) => {
      state.search.selectedBus = action.payload;
    },
    clearSearchResults: (state) => {
      state.search.searchResults = [];
      state.search.selectedBus = null;
      state.search.expandedBus = null;
      state.search.fullApiResponse = null;
      state.search.searchAttempted = false;
    },
    setFilters: (state, action) => {
      state.search.filters = { ...state.search.filters, ...action.payload };
    },
    resetFilters: (state) => {
      state.search.filters = initialState.search.filters;
    },
    setCurrentBooking: (state, action) => {
      state.search.currentBooking = action.payload;
    },
    addToBookingHistory: (state, action) => {
      state.search.bookingHistory.unshift(action.payload);
    },
    clearCurrentBooking: (state) => {
      state.search.currentBooking = null;
    },
    setStaticStates: (state) => {
      state.search.states = STATIC_STATES;
      state.loading.states = false;
      state.errors.states = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOperators.pending, (state) => { state.loading.operators = true; })
      .addCase(fetchOperators.fulfilled, (state, action) => {
        state.loading.operators = false;
        state.operators = action.payload;
      })
      .addCase(fetchOperators.rejected, (state, action) => {
        state.loading.operators = false;
        state.errors.operators = action.error.message;
      })
      .addCase(fetchRoutePaths.pending, (state) => { state.loading.routePaths = true; })
      .addCase(fetchRoutePaths.fulfilled, (state, action) => {
        state.loading.routePaths = false;
        state.routePaths = action.payload;
      })
      .addCase(fetchRoutePaths.rejected, (state, action) => {
        state.loading.routePaths = false;
        state.errors.routePaths = action.error.message;
      })
      .addCase(fetchRoutePathVillages.pending, (state) => { state.loading.routePathVillages = true; })
      .addCase(fetchRoutePathVillages.fulfilled, (state, action) => {
        state.loading.routePathVillages = false;
        state.routePathVillages = action.payload;
      })
      .addCase(fetchRoutePathVillages.rejected, (state, action) => {
        state.loading.routePathVillages = false;
        state.errors.routePathVillages = action.error.message;
      })
      .addCase(checkBusNumberAvailability.pending, (state) => {
        state.loading.checkingAvailability = true;
        state.errors.registration = null;
      })
      .addCase(checkBusNumberAvailability.fulfilled, (state, action) => {
        state.loading.checkingAvailability = false;
        state.busNumberAvailability = action.payload;
      })
      .addCase(checkBusNumberAvailability.rejected, (state, action) => {
        state.loading.checkingAvailability = false;
        state.errors.registration = action.payload || 'Failed to check bus number availability';
        state.busNumberAvailability = null;
      })
      .addCase(fetchStates.pending, (state) => {
        state.loading.states = true;
        state.errors.states = null;
      })
      .addCase(fetchStates.fulfilled, (state, action) => {
        state.loading.states = false;
        if (action.payload && Array.isArray(action.payload)) {
          const validStates = action.payload.filter(stateItem => stateItem && stateItem.stateCode && stateItem.displayName);
          state.search.states = validStates;
          if (validStates.length === 0) state.errors.states = 'No states available';
        } else {
          state.errors.states = 'Invalid states data';
        }
      })
      .addCase(fetchStates.rejected, (state, action) => {
        state.loading.states = false;
        state.errors.states = action.payload || 'Failed to fetch states';
      })
      .addCase(fetchZones.pending, (state) => { state.loading.zones = true; })
      .addCase(fetchZones.fulfilled, (state, action) => {
        state.loading.zones = false;
        state.zones = action.payload;
      })
      .addCase(fetchZones.rejected, (state, action) => {
        state.loading.zones = false;
        state.errors.zones = action.payload;
      })
      .addCase(registerNewBus.pending, (state) => {
        state.loading.registration = true;
        state.errors.registration = null;
        state.successMessage = null;
      })
      .addCase(registerNewBus.fulfilled, (state, action) => {
        state.loading.registration = false;
        state.successMessage = action.payload.message || 'Bus registered successfully!';
        state.currentBus = action.payload;
        state.busNumberAvailability = null;
      })
      .addCase(registerNewBus.rejected, (state, action) => {
        state.loading.registration = false;
        state.errors.registration = action.payload || 'Failed to register bus';
      })
      .addCase(fetchDistrictsByState.pending, (state) => {
        state.loading.districts = true;
        state.errors.districts = null;
      })
      .addCase(fetchDistrictsByState.fulfilled, (state, action) => {
        state.loading.districts = false;
        const { type, districts } = action.payload;
        if (districts && Array.isArray(districts)) {
          const validDistricts = districts.filter(district => district && district.districtCode && district.displayName);
          if (type === 'from') {
            state.search.districts.fromDistricts = validDistricts;
            state.search.cities.fromCities = [];
            state.search.formData.fromDistrict = '';
            state.search.formData.fromCity = '';
          } else {
            state.search.districts.toDistricts = validDistricts;
            state.search.cities.toCities = [];
            state.search.formData.toDistrict = '';
            state.search.formData.toCity = '';
          }
          if (validDistricts.length === 0) state.errors.districts = 'No districts available';
        }
      })
      .addCase(fetchDistrictsByState.rejected, (state, action) => {
        state.loading.districts = false;
        state.errors.districts = action.payload || 'Failed to fetch districts';
      })
      .addCase(fetchCitiesByDistrict.pending, (state) => {
        state.loading.cities = true;
        state.errors.cities = null;
      })
      .addCase(fetchCitiesByDistrict.fulfilled, (state, action) => {
        state.loading.cities = false;
        const { type, cities } = action.payload;
        if (cities && Array.isArray(cities)) {
          const uniqueCities = new Map();
          cities.forEach(item => {
            if (item && item.cityVillageCode) {
              uniqueCities.set(item.cityVillageCode, {
                code: item.cityVillageCode,
                name: item.displayName || item.cityVillageCode,
                hindi: item.hindi,
                displayName: item.displayName
              });
            }
          });
          const citiesData = Array.from(uniqueCities.values());
          if (type === 'from') state.search.cities.fromCities = citiesData;
          else state.search.cities.toCities = citiesData;
          if (citiesData.length === 0) state.errors.cities = 'No cities available';
        }
      })
      .addCase(fetchCitiesByDistrict.rejected, (state, action) => {
        state.loading.cities = false;
        state.errors.cities = action.payload || 'Failed to fetch cities';
      })
      .addCase(searchBuses.pending, (state) => {
        state.loading.buses = true;
        state.errors.buses = null;
        state.search.searchResults = [];
        state.search.fullApiResponse = null;
        state.search.searchAttempted = true;
      })
      .addCase(searchBuses.fulfilled, (state, action) => {
        state.loading.buses = false;
        const data = action.payload;
        state.search.fullApiResponse = data;

        // ============================================================
        // NEW API STRUCTURE: data has 'buses', 'arrival', 'return'
        //
        // arrival[] = FORWARD ROUTE (filtered per bus by busNo)
        //   - FIRST entry (ascending by time) = departure from source
        //   - LAST  entry (ascending by time) = arrival at destination
        //
        // return[] = RETURN ROUTE (filtered per bus by busNo)
        //   - LAST entry's departureTime = final return departure
        //
        // ✅ FIX: Use String(...) comparison for busNo so each bus gets
        //         its own correct timings instead of shared first/last
        //         entries from the entire arrival array.
        // ============================================================
        if (data && data.buses && Array.isArray(data.buses)) {
          const arrivalData = data.arrival || [];
          const returnData = data.return || [];

          const formattedResults = data.buses
            .filter(bus => bus)
            .map((bus, index) => {
              const isActive = bus.status === true;

              // ✅ Filter timings for THIS bus only (String compare for safety)
              const busArrivalTimings = arrivalData
                .filter(timing => String(timing.busNo) === String(bus.busNo))
                .sort((a, b) => {
                  const timeA = a.arrivalTime || a.departureTime || '';
                  const timeB = b.arrivalTime || b.departureTime || '';
                  return timeA.localeCompare(timeB);
                });

              const busReturnTimings = returnData
                .filter(timing => String(timing.busNo) === String(bus.busNo))
                .sort((a, b) => {
                  const timeA = a.departureTime || a.arrivalTime || '';
                  const timeB = b.departureTime || b.arrivalTime || '';
                  return timeA.localeCompare(timeB);
                });

              // ------------------------------------------------------------------
              // FORWARD ROUTE (from arrival[])
              // - first stop = departure from source
              // - last  stop = arrival at destination
              // ------------------------------------------------------------------
              const firstForwardStop = busArrivalTimings[0];
              const lastForwardStop = busArrivalTimings[busArrivalTimings.length - 1];

              const departureTime =
                firstForwardStop?.arrivalTime ||
                firstForwardStop?.departureTime ||
                'N/A';
              const arrivalTime =
                lastForwardStop?.arrivalTime ||
                lastForwardStop?.departureTime ||
                'N/A';

              // ------------------------------------------------------------------
              // RETURN ROUTE (from return[])
              // - LAST entry's departureTime = final return departure
              // ------------------------------------------------------------------
              const lastReturnStop = busReturnTimings[busReturnTimings.length - 1];
              const firstReturnStop = busReturnTimings[0];
              const returnTime =
                lastReturnStop?.departureTime ||
                lastReturnStop?.arrivalTime ||
                firstReturnStop?.departureTime ||
                firstReturnStop?.arrivalTime ||
                'N/A';

              // Duration = arrivalTime - departureTime
              let duration = 'N/A';
              if (departureTime !== 'N/A' && arrivalTime !== 'N/A') {
                try {
                  const depTime = new Date(`2000-01-01T${departureTime}`);
                  const arrTime = new Date(`2000-01-01T${arrivalTime}`);
                  if (arrTime < depTime) arrTime.setDate(arrTime.getDate() + 1);
                  const diffMs = arrTime - depTime;
                  const hours = Math.floor(diffMs / (1000 * 60 * 60));
                  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                  duration = `${hours}h ${minutes}m`;
                } catch (error) {
                  duration = 'N/A';
                }
              }

              const seatsAvailable = isActive ? Math.floor(Math.random() * 40) + 5 : 0;
              const price =
                bus.busName && bus.busName.toLowerCase().includes('volvo') ? 1800 : 1200;
              const busType =
                bus.busName && bus.busName.toLowerCase().includes('sleeper')
                  ? 'Sleeper'
                  : 'Seater';
              const amenities =
                bus.busName && bus.busName.toLowerCase().includes('volvo')
                  ? ['AC', 'Wifi', 'Charging', 'Water']
                  : ['AC', 'Water'];
              const operatorName =
                bus.operator?.displayName ||
                bus.operator?.operatorName ||
                'Unknown Operator';

              return {
                id: bus.busNo || `bus-${index + 1}`,
                name: bus.displayName || bus.busName || `Bus ${index + 1}`,
                operator: operatorName,
                busNumber:
                  bus.busRegistrationNumber ||
                  `BUS-${String(index + 1).padStart(3, '0')}`,
                contact:
                  bus.busContactNumber ||
                  bus.operator?.busOperatorContactNumber ||
                  'N/A',
                seatsAvailable,
                price,
                rating: (4.0 + Math.random() * 0.5).toFixed(1),
                amenities,
                type: busType,
                isActive,
                departure: departureTime, // Forward: first arrival stop time
                arrival: arrivalTime,     // Forward: last arrival stop time
                returnTime: returnTime,   // Return: last return entry's departure time
                duration,
                rawBusData: bus
              };
            });
          state.search.searchResults = formattedResults;
          if (formattedResults.length === 0)
            state.errors.buses = 'No buses found for this route';
        } else {
          state.errors.buses = 'No bus data available';
        }
      })
      .addCase(searchBuses.rejected, (state, action) => {
        state.loading.buses = false;
        state.errors.buses = action.payload || 'Failed to search buses';
        state.search.fullApiResponse = null;
        state.search.searchAttempted = true;
      });
  }
});

export const {
  setLanguage,
  clearMessages,
  setCurrentBus,
  filterZonesByStateCode,
  clearBusNumberAvailability,
  setSearchFormData,
  resetSearchFormData,
  resetSearchPage,
  setSearchStates,
  setSearchDistricts,
  setSearchCities,
  setSearchResults,
  setExpandedBus,
  selectSearchBus,
  clearSearchResults,
  setFilters,
  resetFilters,
  setCurrentBooking,
  addToBookingHistory,
  clearCurrentBooking,
  setStaticStates
} = busSlice.actions;

export const selectLanguage = (state) => state.bus.language;
export const selectOperators = (state) => state.bus.operators;
export const selectRoutePaths = (state) => state.bus.routePaths;
export const selectRoutePathVillages = (state) => state.bus.routePathVillages;
export const selectZones = (state) => state.bus.zones;
export const selectFilteredZones = (state) => state.bus.filteredZones;
export const selectCurrentBus = (state) => state.bus.currentBus;
export const selectBusNumberAvailability = (state) => state.bus.busNumberAvailability;
export const selectSearchFormData = (state) => state.bus.search.formData;
export const selectSearchStates = (state) => state.bus.search.states;
export const selectSearchDistricts = (state) => state.bus.search.districts;
export const selectSearchCities = (state) => state.bus.search.cities;
export const selectSearchResults = (state) => state.bus.search.searchResults;
export const selectFullApiResponse = (state) => state.bus.search.fullApiResponse;
export const selectSearchAttempted = (state) => state.bus.search.searchAttempted;
export const selectExpandedBus = (state) => state.bus.search.expandedBus;
export const selectSelectedSearchBus = (state) => state.bus.search.selectedBus;
export const selectFilters = (state) => state.bus.search.filters;
export const selectCurrentBooking = (state) => state.bus.search.currentBooking;
export const selectBookingHistory = (state) => state.bus.search.bookingHistory;
export const selectLoadingStates = (state) => state.bus.loading.states;
export const selectLoadingDistricts = (state) => state.bus.loading.districts;
export const selectLoadingCities = (state) => state.bus.loading.cities;
export const selectLoadingBuses = (state) => state.bus.loading.buses;
export const selectLoadingOperators = (state) => state.bus.loading.operators;
export const selectLoadingRoutePaths = (state) => state.bus.loading.routePaths;
export const selectLoadingRoutePathVillages = (state) => state.bus.loading.routePathVillages;
export const selectLoadingZones = (state) => state.bus.loading.zones;
export const selectLoadingRegistration = (state) => state.bus.loading.registration;
export const selectCheckingAvailability = (state) => state.bus.loading.checkingAvailability;
export const selectErrors = (state) => state.bus.errors;
export const selectSuccessMessage = (state) => state.bus.successMessage;

export default busSlice.reducer;